import { useCallback, useState } from "react";
import { AxiosError } from "axios";

import { AddOrEditPerson } from "../../Api/Person"; // adjust relative path
import { IPerson } from "../../Models/Interfaces/IPerson";

type UsePersonUpdateArgs = {
    getValidAccessToken: () => Promise<string | null>;
    userId: string | null | undefined;
};

type UpdateResult =
    | { ok: true; updated: IPerson }
    | { ok: false; error?: AxiosError | Error };

export function useUpdatePerson({ getValidAccessToken, userId }: UsePersonUpdateArgs) {
    const [isUpdating, setIsUpdating] = useState(false);

    const updatePerson = useCallback(
        async (base: IPerson, changes: Partial<IPerson>): Promise<UpdateResult> => {
            if (!userId) {
                console.error("useUpdatePerson: missing userId");
                return { ok: false };
            }

            const controller = new AbortController();
            setIsUpdating(true);

            try {
                const token = await getValidAccessToken();
                if (!token) {
                    console.error("useUpdatePerson: no valid access token");
                    return { ok: false };
                }

                const updatedPerson: IPerson = {
                    ...base,
                    ...changes,
                };

                const result = await AddOrEditPerson(
                    userId,
                    changes, // send only changed fields; backend merges
                    token,
                    controller
                );

                if (result === true) {
                    return { ok: true, updated: updatedPerson };
                } else {
                    return { ok: false, error: result }; // <- TS: "result might be false"
                }
            } catch (err) {
                console.error("Failed to update person", err);
                return { ok: false, error: err as Error };
            } finally {
                controller.abort();
                setIsUpdating(false);
            }
        },
        [getValidAccessToken, userId]
    );

    return { updatePerson, isUpdating };
}
